import React, { useState } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Select,
    Button,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Statistic,
    Modal,
    message,
    Popconfirm,
    Tooltip
} from 'antd';
import {
    ReloadOutlined,
    DeleteOutlined,
    EyeOutlined,
    FilterOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const AuditLogs = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [filters, setFilters] = useState({
        page: 1,
        limit: 50,
        action: null,
        resource: null,
        user: null,
        status: null,
        startDate: null,
        endDate: null
    });
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // Fetch audit logs
    const { data: logsData, isLoading, refetch: refetchLogs } = useQuery(
        ['auditLogs', filters],
        async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });
            const response = await api.get(`/audit-logs?${params}`);
            return response.data;
        },
        {
            keepPreviousData: true,
            cacheTime: 0,
            staleTime: 0
        }
    );

    // Fetch statistics
    const { data: statsData, refetch: refetchStats } = useQuery(
        ['auditLogStats', filters.startDate, filters.endDate],
        async () => {
            const params = new URLSearchParams();
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            const response = await api.get(`/audit-logs/stats?${params}`);
            return response.data;
        },
        {
            cacheTime: 0,
            staleTime: 0
        }
    );

    // Delete old logs mutation
    const deleteOldLogsMutation = useMutation(
        (days) => api.delete(`/audit-logs/old/${days}`),
        {
            onSuccess: () => {
                message.success(t('oldLogsDeletedSuccessfully'));
                queryClient.invalidateQueries('auditLogs');
                queryClient.invalidateQueries('auditLogStats');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('errorDeletingLogs'));
            }
        }
    );

    const actionColors = {
        CREATE: 'green',
        UPDATE: 'blue',
        DELETE: 'red',
        LOGIN: 'cyan',
        LOGOUT: 'default',
        EXPORT: 'purple',
        IMPORT: 'orange',
        GENERATE_CERTIFICATE: 'gold',
        GENERATE_INVOICE: 'magenta',
        PAYMENT_RECEIVED: 'lime',
        BACKUP_CREATED: 'geekblue',
        BACKUP_RESTORED: 'volcano',
        SETTINGS_CHANGED: 'orange',
        USER_CREATED: 'green',
        USER_UPDATED: 'blue',
        USER_DELETED: 'red'
    };

    const statusColors = {
        SUCCESS: 'success',
        FAILED: 'error'
    };

    const columns = [
        {
            title: t('timestamp'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        },
        {
            title: t('user'),
            dataIndex: 'user',
            key: 'user',
            width: 150,
            render: (user) => user ? `${user.firstName} ${user.lastName}` : 'N/A'
        },
        {
            title: t('action'),
            dataIndex: 'action',
            key: 'action',
            width: 150,
            render: (action) => (
                <Tag color={actionColors[action] || 'default'}>
                    {action}
                </Tag>
            )
        },
        {
            title: t('resource'),
            dataIndex: 'resource',
            key: 'resource',
            width: 120
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={statusColors[status]}>
                    {status}
                </Tag>
            )
        },
        {
            title: t('ipAddress'),
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 140
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Tooltip title={t('viewDetails')}>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => {
                            setSelectedLog(record);
                            setDetailsVisible(true);
                        }}
                    />
                </Tooltip>
            )
        }
    ];

    const handleDateRangeChange = (dates) => {
        setFilters({
            ...filters,
            startDate: dates ? dates[0].toISOString() : null,
            endDate: dates ? dates[1].toISOString() : null,
            page: 1
        });
    };

    const handleExport = () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value && key !== 'page' && key !== 'limit') params.append(key, value);
        });
        window.open(`${api.defaults.baseURL}/audit-logs/export?${params}`, '_blank');
    };

    return (
        <div>
            <div className="page-header">
                <Title level={2} style={{ margin: 0 }}>{t('auditLogs')}</Title>
            </div>

            {/* Statistics Cards */}
            {statsData?.data && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('totalActions')}
                                value={statsData.data.byAction.reduce((sum, item) => sum + item.count, 0)}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('successfulActions')}
                                value={statsData.data.byStatus.find(s => s._id === 'SUCCESS')?.count || 0}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('failedActions')}
                                value={statsData.data.byStatus.find(s => s._id === 'FAILED')?.count || 0}
                                valueStyle={{ color: '#cf1322' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('uniqueUsers')}
                                value={statsData.data.byUser.length}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            <Card>
                {/* Filters */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={handleDateRangeChange}
                            placeholder={[t('startDate'), t('endDate')]}
                        />
                    </Col>
                    <Col span={4}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('selectAction')}
                            allowClear
                            value={filters.action}
                            onChange={(value) => setFilters({ ...filters, action: value, page: 1 })}
                        >
                            <Option value="CREATE">CREATE</Option>
                            <Option value="UPDATE">UPDATE</Option>
                            <Option value="DELETE">DELETE</Option>
                            <Option value="LOGIN">LOGIN</Option>
                            <Option value="LOGOUT">LOGOUT</Option>
                            <Option value="EXPORT">EXPORT</Option>
                            <Option value="GENERATE_CERTIFICATE">GENERATE_CERTIFICATE</Option>
                            <Option value="PAYMENT_RECEIVED">PAYMENT_RECEIVED</Option>
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('selectResource')}
                            allowClear
                            value={filters.resource}
                            onChange={(value) => setFilters({ ...filters, resource: value, page: 1 })}
                        >
                            <Option value="Student">Student</Option>
                            <Option value="Course">Course</Option>
                            <Option value="Staff">Staff</Option>
                            <Option value="Income">Income</Option>
                            <Option value="Expense">Expense</Option>
                            <Option value="Invoice">Invoice</Option>
                            <Option value="Certificate">Certificate</Option>
                            <Option value="User">User</Option>
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('selectStatus')}
                            allowClear
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
                        >
                            <Option value="SUCCESS">SUCCESS</Option>
                            <Option value="FAILED">FAILED</Option>
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    refetchLogs();
                                    refetchStats();
                                    message.success(t('refreshed') || 'Refreshed');
                                }}
                            >
                                {t('refresh')}
                            </Button>
                            <Popconfirm
                                title={t('deleteLogsOlderThan90Days')}
                                onConfirm={() => deleteOldLogsMutation.mutate(90)}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleteOldLogsMutation.isLoading}
                                >
                                    {t('cleanOld')}
                                </Button>
                            </Popconfirm>
                        </Space>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={logsData?.data || []}
                    rowKey="_id"
                    loading={isLoading}
                    scroll={{ x: 1200 }}
                    pagination={{
                        current: filters.page,
                        pageSize: filters.limit,
                        total: logsData?.total || 0,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')} ${total} ${t('items')}`,
                        onChange: (page, pageSize) => {
                            setFilters({ ...filters, page, limit: pageSize });
                        }
                    }}
                />
            </Card>

            {/* Details Modal */}
            <Modal
                title={t('auditLogDetails')}
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                footer={null}
                width={700}
            >
                {selectedLog && (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Text strong>{t('timestamp')}:</Text>
                                <div>{dayjs(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}</div>
                            </Col>
                            <Col span={12}>
                                <Text strong>{t('user')}:</Text>
                                <div>{selectedLog.user ? `${selectedLog.user.firstName} ${selectedLog.user.lastName}` : 'N/A'}</div>
                            </Col>
                            <Col span={12}>
                                <Text strong>{t('action')}:</Text>
                                <div><Tag color={actionColors[selectedLog.action]}>{selectedLog.action}</Tag></div>
                            </Col>
                            <Col span={12}>
                                <Text strong>{t('resource')}:</Text>
                                <div>{selectedLog.resource}</div>
                            </Col>
                            <Col span={12}>
                                <Text strong>{t('status')}:</Text>
                                <div><Tag color={statusColors[selectedLog.status]}>{selectedLog.status}</Tag></div>
                            </Col>
                            <Col span={12}>
                                <Text strong>{t('ipAddress')}:</Text>
                                <div>{selectedLog.ipAddress || 'N/A'}</div>
                            </Col>
                            <Col span={24}>
                                <Text strong>{t('userAgent')}:</Text>
                                <div style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                                    {selectedLog.userAgent || 'N/A'}
                                </div>
                            </Col>
                            {selectedLog.errorMessage && (
                                <Col span={24}>
                                    <Text strong type="danger">{t('errorMessage')}:</Text>
                                    <div style={{ color: '#ff4d4f' }}>{selectedLog.errorMessage}</div>
                                </Col>
                            )}
                            <Col span={24}>
                                <Text strong>{t('details')}:</Text>
                                <pre style={{
                                    background: '#f5f5f5',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    maxHeight: '300px',
                                    overflow: 'auto'
                                }}>
                                    {JSON.stringify(selectedLog.details, null, 2)}
                                </pre>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AuditLogs;
